import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';

import { LegFiJwtService } from '../auth/legfi-jwt.service';
import { LocalService } from '../storage/local.service';
import { USER_PREFERENCE_KEYS, UserPreferencesService } from '../user-preferences.service';
import {
    AiConversationStreamHandle,
    AiPocStreamService,
    AiStreamEvent,
    AssistantPocPendingQuestions,
} from './ai-poc-stream.service';
import {
    ActionPreviewEdits,
    AiPocService,
    AssistantPocChatResponse,
    AssistantPocCitation,
    AssistantPocFeedback,
    AssistantPocPendingProposal,
    AssistantPocQuestionAnswer,
    AssistantPocToolCard,
    CARD_PRODUCING_TOOLS,
} from './ai-poc.service';

/**
 * Per-message chat row rendered inside the Gable drawer. Mirrors the shape
 * previously local to `AiPocPageComponent` — kept here verbatim so the drawer
 * host stays a thin view over service state.
 */
export interface GableAppliedLesson {
    id: number;
    title: string;
}

export interface GableChatMessage {
    role: 'user' | 'assistant' | 'tool';
    content: string;
    citations?: AssistantPocCitation[];
    toolsUsed?: string[];
    timestamp: Date;
    alert?: 'success' | 'warn' | 'info';
    messageId?: number;
    streamState?: 'streaming' | 'complete' | 'failed';
    toolName?: string;
    toolCallId?: string;
    toolOk?: boolean;
    thoughts?: GableThought[];
    thoughtsCollapsed?: boolean;
    streamStartedAt?: number;
    streamEndedAt?: number;
    thinkingContent?: string;
    interactionId?: number;
    toolCards?: AssistantPocToolCard[];
    /** Lessons that fired on this turn — surfaces "Applied a domain note" indicator. */
    appliedLessons?: GableAppliedLesson[];
}

export interface GableThought {
    id: string;
    label: string;
    ok?: boolean;
    /**
     * 'tool' — structural tool-call row.
     * 'summary' — layperson-safe paraphrase of one phase of model reasoning,
     *             produced by the Haiku summarizer + denylist pipeline.
     */
    kind?: 'tool' | 'summary';
}

/** Clamp the drawer width to a sane range so the panel stays usable. */
const MIN_WIDTH_PX = 320;
const MAX_WIDTH_PX = 720;
const DEFAULT_WIDTH_PX = 400;

/** Display mode for the Gable surface. */
export type GableMode = 'drawer' | 'expanded';
const DEFAULT_MODE: GableMode = 'drawer';

/**
 * Safety-net watchdog: forcibly finalize stuck streams. Mirrors the page-
 * scoped watchdog the POC used; lives in the service now so it survives
 * navigation between routes.
 */
const STREAM_WATCHDOG_MS = 90_000;

/**
 * Singleton coordinator for the omnipresent Gable AI drawer.
 *
 * - Owns open / close state and the persisted user-resizable width.
 * - Owns the full assistant conversation (messages, streaming bubble, pending
 *   proposals, pending question card) so chat state survives route changes.
 * - Brokers all streaming/Echo subscription lifecycle so navigating mid-turn
 *   doesn't tear the channel down out from under the active stream.
 *
 * The Gable drawer host component is a thin view: it subscribes to the
 * observables here, dispatches user input via the public methods, and never
 * holds chat state of its own.
 */
@Injectable({
    providedIn: 'root',
})
export class GableDrawerService {
    // -------------------------------------------------------------------------
    // Open / close + width
    // -------------------------------------------------------------------------

    private readonly _isOpen$ = new BehaviorSubject<boolean>(false);
    readonly isOpen$: Observable<boolean> = this._isOpen$.asObservable();

    private readonly _widthPx$ = new BehaviorSubject<number>(DEFAULT_WIDTH_PX);
    readonly widthPx$: Observable<number> = this._widthPx$.asObservable();

    /**
     * Display mode: 'drawer' (right-edge slide) or 'expanded' (full-width
     * centered dialog). User-toggleable from the drawer header and
     * persisted via UserPreferencesService.
     */
    private readonly _mode$ = new BehaviorSubject<GableMode>(DEFAULT_MODE);
    readonly mode$: Observable<GableMode> = this._mode$.asObservable();

    /**
     * Inline drawer view — `'chat'` is the normal conversation, `'teach'`
     * swaps the entire drawer body to the Teach Gable form (back-arrow in
     * the header returns to chat). Only used in drawer mode; expanded /
     * dialog mode opens Teach as a separate MatDialog.
     */
    private readonly _drawerView$ = new BehaviorSubject<'chat' | 'teach'>('chat');
    readonly drawerView$: Observable<'chat' | 'teach'> = this._drawerView$.asObservable();

    /** Data payload for the inline Teach view. Set by openTeachInDrawer(). */
    private _teachData: import('../../components/v2-layout/ai-poc/teach-gable-dialog/teach-gable-dialog.component').TeachGableDialogData | null = null;

    get teachData(): import('../../components/v2-layout/ai-poc/teach-gable-dialog/teach-gable-dialog.component').TeachGableDialogData | null {
        return this._teachData;
    }

    private readonly _draftMessage$ = new BehaviorSubject<string>('');
    readonly draftMessage$: Observable<string> = this._draftMessage$.asObservable();

    // -------------------------------------------------------------------------
    // Chat state — exposed as observables so the host can render with OnPush.
    // -------------------------------------------------------------------------

    private readonly _messages$ = new BehaviorSubject<GableChatMessage[]>([]);
    readonly messages$: Observable<GableChatMessage[]> = this._messages$.asObservable();

    private readonly _pendingProposals$ = new BehaviorSubject<AssistantPocPendingProposal[]>([]);
    readonly pendingProposals$: Observable<AssistantPocPendingProposal[]> = this._pendingProposals$.asObservable();

    private readonly _pendingQuestions$ = new BehaviorSubject<AssistantPocPendingQuestions | null>(null);
    readonly pendingQuestions$: Observable<AssistantPocPendingQuestions | null> = this._pendingQuestions$.asObservable();

    private readonly _isSending$ = new BehaviorSubject<boolean>(false);
    readonly isSending$: Observable<boolean> = this._isSending$.asObservable();

    private readonly _apiSuggestions$ = new BehaviorSubject<string[]>([]);
    readonly apiSuggestions$: Observable<string[]> = this._apiSuggestions$.asObservable();

    private readonly _feedbackByMessageId = new Map<number, AssistantPocFeedback>();
    private readonly _feedback$ = new BehaviorSubject<Map<number, AssistantPocFeedback>>(this._feedbackByMessageId);
    readonly feedback$: Observable<Map<number, AssistantPocFeedback>> = this._feedback$.asObservable();

    conversationId: number | null = null;
    submittingAnswers = false;
    proposalInFlight: number | null = null;

    private _streamHandle: AiConversationStreamHandle | null = null;
    private _streamSub: Subscription | null = null;
    /**
     * Message id of the assistant bubble currently receiving deltas.
     * Captured at stream-start so `cancelStream()` can POST a cancel
     * signal scoped to that message — independent of whatever's
     * happening in the UI when the user clicks Stop.
     */
    private _streamingAssistantMessageId: number | null = null;
    private _streamWatchdog: ReturnType<typeof setTimeout> | null = null;

    constructor(
        private _service: AiPocService,
        private _streamService: AiPocStreamService,
        private _prefs: UserPreferencesService,
    ) {
        // Hydrate width + mode from persisted user prefs. Done in the
        // constructor (rather than as field initializers) so we use the
        // injected service rather than touching localStorage directly.
        const persistedWidth = this._prefs.getPreference<number>(
            USER_PREFERENCE_KEYS.GABLE_DRAWER_WIDTH,
            DEFAULT_WIDTH_PX,
        );
        this._widthPx$.next(this._clampWidth(persistedWidth));
        const persistedMode = this._prefs.getPreference<GableMode>(
            USER_PREFERENCE_KEYS.GABLE_DRAWER_MODE,
            DEFAULT_MODE,
        );
        this._mode$.next(persistedMode === 'expanded' ? 'expanded' : 'drawer');
    }

    // -------------------------------------------------------------------------
    // Feature flag / open-close
    // -------------------------------------------------------------------------

    /** Synchronous feature-flag check. Backed by JWT claims. */
    get flagEnabled(): boolean {
        return LegFiJwtService.featureEnabled('ai-gable.enabled');
    }

    open(): void {
        if (!this._isOpen$.value) {
            this._isOpen$.next(true);
        }
    }

    close(): void {
        if (this._isOpen$.value) {
            this._isOpen$.next(false);
        }
    }

    toggle(): void {
        this._isOpen$.next(!this._isOpen$.value);
    }

    /** Open the drawer and prefill the composer with the given prompt. */
    seed(prompt: string): void {
        this._draftMessage$.next(prompt ?? '');
        this.open();
    }

    setDraft(text: string): void {
        this._draftMessage$.next(text);
    }

    // -------------------------------------------------------------------------
    // Width (clamped + persisted)
    // -------------------------------------------------------------------------

    setWidth(px: number): void {
        const clamped = this._clampWidth(px);
        if (clamped === this._widthPx$.value) {
            return;
        }
        this._widthPx$.next(clamped);
        this._prefs.setPreference(USER_PREFERENCE_KEYS.GABLE_DRAWER_WIDTH, clamped);
    }

    get widthPx(): number {
        return this._widthPx$.value;
    }

    private _clampWidth(px: number): number {
        return Math.min(MAX_WIDTH_PX, Math.max(MIN_WIDTH_PX, Math.round(px)));
    }

    // -------------------------------------------------------------------------
    // Display mode (drawer | expanded dialog)
    // -------------------------------------------------------------------------

    get mode(): GableMode {
        return this._mode$.value;
    }

    setMode(mode: GableMode): void {
        if (mode === this._mode$.value) {
            return;
        }
        this._mode$.next(mode);
        this._prefs.setPreference(USER_PREFERENCE_KEYS.GABLE_DRAWER_MODE, mode);
    }

    /**
     * Flip between drawer + expanded modes. Bound to the dock-toggle button
     * in the drawer header so a single click pops out into the full-width
     * dialog (and back).
     */
    toggleMode(): void {
        this.setMode(this._mode$.value === 'drawer' ? 'expanded' : 'drawer');
    }

    // -------------------------------------------------------------------------
    // Conversation lifecycle
    // -------------------------------------------------------------------------

    /** Wipe the conversation back to a fresh empty state. */
    startNewConversation(): void {
        this._teardownStream();
        this.conversationId = null;
        this._messages$.next([]);
        this._pendingQuestions$.next(null);
        this._apiSuggestions$.next([]);
        this._isSending$.next(false);
        this._draftMessage$.next('');
        this._feedbackByMessageId.clear();
        this._feedback$.next(new Map(this._feedbackByMessageId));
    }

    get messages(): GableChatMessage[] {
        return this._messages$.value;
    }

    get isSending(): boolean {
        return this._isSending$.value;
    }

    get pendingQuestions(): AssistantPocPendingQuestions | null {
        return this._pendingQuestions$.value;
    }

    /** Load pending proposals from the server (idempotent; non-fatal on error). */
    loadPendingProposals(): void {
        this._service.listPendingProposals().subscribe({
            next: (res) => this._pendingProposals$.next(res.proposals ?? []),
            error: () => this._pendingProposals$.next([]),
        });
    }

    // -------------------------------------------------------------------------
    // Send (streaming, with blocking fallback)
    // -------------------------------------------------------------------------

    sendMessage(text: string): void {
        const trimmed = (text ?? '').trim();
        if (!trimmed || this._isSending$.value) {
            return;
        }
        // Mid-question-card free-text discards the open batch.
        if (this._pendingQuestions$.value !== null) {
            this._pendingQuestions$.next(null);
        }
        this._messages$.next([
            ...this._messages$.value,
            { role: 'user', content: trimmed, timestamp: new Date() },
        ]);
        this._draftMessage$.next('');
        this._isSending$.next(true);
        this._teardownStream();

        this._service.chatStream(trimmed, this.conversationId).subscribe({
            next: (handshake) => {
                this.conversationId = handshake.conversationId;
                this._beginStreamingTurn(handshake.conversationId, handshake.assistantMessageId);
            },
            error: () => this._sendBlockingFallback(trimmed),
        });
    }

    private _sendBlockingFallback(text: string): void {
        this._service.chat(text, this.conversationId).subscribe({
            next: (res) => {
                this._isSending$.next(false);
                this._processChatEnvelope(res);
            },
            error: () => {
                this._isSending$.next(false);
                this._messages$.next([
                    ...this._messages$.value,
                    {
                        role: 'assistant',
                        content: 'Sorry — I couldn\'t reach the assistant just now. Please try that again in a moment.',
                        timestamp: new Date(),
                        streamState: 'failed',
                    },
                ]);
            },
        });
    }

    private _beginStreamingTurn(conversationId: number, assistantMessageId: number): void {
        this._streamingAssistantMessageId = assistantMessageId;
        const existing = this._messages$.value.find(
            (m) => m.role === 'assistant' && m.messageId === assistantMessageId,
        );
        if (!existing) {
            this._messages$.next([
                ...this._messages$.value,
                {
                    role: 'assistant',
                    content: '',
                    timestamp: new Date(),
                    messageId: assistantMessageId,
                    streamState: 'streaming',
                },
            ]);
        }

        const handle = this._streamService.open(conversationId);
        this._streamHandle = handle;
        this._streamSub = handle.events$.subscribe({
            next: (event) => this._handleStreamEvent(event),
        });
    }

    /**
     * User-driven stop: fired by the composer's interrupt button while a
     * turn is streaming. Posts a cancel signal scoped to the in-flight
     * assistant message id (the orchestrator's worker polls a cache flag
     * keyed on that id and short-circuits its Prism loop), then tears
     * down the local stream subscription so the UI freezes immediately
     * — the user shouldn't have to wait for the server to settle to
     * regain the composer. Whatever text streamed in so far stays in
     * the bubble; the stream-state flips to 'complete' so footer
     * affordances (thumbs, follow-ups) render.
     */
    cancelStream(): void {
        const msgId = this._streamingAssistantMessageId;
        if (msgId === null) {
            // Nothing to cancel (race with stream completion / failed
            // before we tracked an id). Still clear sending state so the
            // composer unfreezes.
            this._isSending$.next(false);
            return;
        }
        this._service.cancelStream(msgId).subscribe({
            error: () => {
                // Best-effort. The local teardown below still happens.
            },
        });
        this._messages$.next(
            this._messages$.value.map((m) =>
                m.role === 'assistant' && m.messageId === msgId
                    ? { ...m, streamState: 'complete' as const, streamEndedAt: Date.now() }
                    : m,
            ),
        );
        this._isSending$.next(false);
        this._teardownStream();
        this._streamingAssistantMessageId = null;
    }

    private _handleStreamEvent(event: AiStreamEvent): void {
        const messages = this._messages$.value;
        switch (event.type) {
            case 'ai.stream.started': {
                this._armStreamWatchdog();
                const startedAt = Date.parse(event.startedAt);
                const exists = messages.some(
                    (m) => m.role === 'assistant' && m.messageId === event.messageId,
                );
                if (!exists) {
                    this._messages$.next([
                        ...messages,
                        {
                            role: 'assistant',
                            content: '',
                            timestamp: new Date(),
                            messageId: event.messageId,
                            streamState: 'streaming',
                            thoughts: [],
                            thoughtsCollapsed: true,
                            streamStartedAt: Number.isFinite(startedAt) ? startedAt : Date.now(),
                        },
                    ]);
                } else {
                    this._messages$.next(messages.map((m) =>
                        m.role === 'assistant' && m.messageId === event.messageId
                            ? {
                                ...m,
                                thoughts: m.thoughts ?? [],
                                thoughtsCollapsed: m.thoughtsCollapsed ?? true,
                                streamStartedAt: m.streamStartedAt
                                    ?? (Number.isFinite(startedAt) ? startedAt : Date.now()),
                            }
                            : m,
                    ));
                }
                break;
            }
            case 'ai.stream.text-delta': {
                this._messages$.next(messages.map((m) => {
                    if (m.role === 'assistant' && m.messageId === event.messageId) {
                        return { ...m, content: m.content + event.delta };
                    }
                    return m;
                }));
                break;
            }
            case 'ai.stream.tool-call': {
                // event.messageId here is the TOOL row's id, not the
                // assistant bubble's. Attach the thought to the
                // currently-streaming assistant message instead.
                const streamingId = this._streamingAssistantMessageId;
                this._messages$.next(messages.map((m) => {
                    if (m.role === 'assistant' && streamingId !== null && m.messageId === streamingId) {
                        const next = [...(m.thoughts ?? []), { id: event.toolCallId, label: event.label }];
                        return { ...m, thoughts: next };
                    }
                    return m;
                }));
                break;
            }
            case 'ai.stream.tool-result': {
                const streamingId = this._streamingAssistantMessageId;
                this._messages$.next(messages.map((m) => {
                    if (m.role === 'assistant' && streamingId !== null && m.messageId === streamingId && m.thoughts) {
                        const next = m.thoughts.map((t) =>
                            t.id === event.toolCallId ? { ...t, ok: event.ok } : t,
                        );
                        return { ...m, thoughts: next };
                    }
                    return m;
                }));
                break;
            }
            case 'ai.stream.thinking-delta': {
                // Backend no longer broadcasts raw thinking (it's leaky).
                // Keep this case as a no-op so any in-flight stream from an
                // older server build doesn't render raw CoT in the UI.
                break;
            }
            case 'ai.stream.summary-delta': {
                const streamingId = this._streamingAssistantMessageId;
                const targetId = event.messageId ?? streamingId;
                this._messages$.next(messages.map((m) => {
                    if (m.role !== 'assistant' || targetId === null || m.messageId !== targetId) {
                        return m;
                    }
                    const existing = m.thoughts ?? [];
                    const summaryEntry = {
                        id: 'summary:' + (event.phaseToolCallId ?? 'final:' + Date.now()),
                        label: event.summary,
                        kind: 'summary' as const,
                    };
                    // If the summary precedes a known tool call, splice it
                    // right before that tool row so visual order matches the
                    // "thought before action" flow. Otherwise append.
                    if (event.phaseToolCallId) {
                        const idx = existing.findIndex((t) => t.id === event.phaseToolCallId);
                        if (idx >= 0) {
                            const next = [
                                ...existing.slice(0, idx),
                                summaryEntry,
                                ...existing.slice(idx),
                            ];
                            return { ...m, thoughts: next };
                        }
                    }
                    return { ...m, thoughts: [...existing, summaryEntry] };
                }));
                break;
            }
            case 'ai.stream.completed': {
                this._messages$.next(messages.map((m) => {
                    if (m.role !== 'assistant') {
                        return m;
                    }
                    const isPrimary = m.messageId === event.messageId;
                    const isOrphanStream = !isPrimary && m.streamState === 'streaming';
                    if (!isPrimary && !isOrphanStream) {
                        return m;
                    }
                    const settledThoughts = (m.thoughts ?? []).map((t) =>
                        t.ok === undefined ? { ...t, ok: true } : t,
                    );
                    return {
                        ...m,
                        streamState: 'complete' as const,
                        toolsUsed: isPrimary ? (event.toolsUsed ?? []) : (m.toolsUsed ?? []),
                        streamEndedAt: Date.now(),
                        thoughts: settledThoughts,
                        // Auto-collapse the thought trail the moment the
                        // stream completes — once the model has produced
                        // its answer, the rationale rows are just visual
                        // noise. User can re-expand via the caret.
                        thoughtsCollapsed: true,
                        interactionId: isPrimary
                            ? (event.aiInteractionEventId ?? m.interactionId)
                            : m.interactionId,
                        toolCards: isPrimary
                            ? ((event.toolCards as AssistantPocToolCard[] | undefined) ?? m.toolCards)
                            : m.toolCards,
                        appliedLessons: isPrimary
                            ? ((event as { appliedLessons?: GableAppliedLesson[] }).appliedLessons ?? m.appliedLessons)
                            : m.appliedLessons,
                    };
                }));
                this._apiSuggestions$.next(event.followUpSuggestions ?? []);

                // Pusher's ~10KB per-event limit silently drops big
                // toolCards payloads (a full P&L row tree can run 40–80KB).
                // When the model used a tool that should produce a card
                // but the broadcast came through with none, fall back to
                // the REST endpoint that reads the persisted card from
                // the assistant message's metadata.
                const broadcastCards = (event.toolCards ?? []) as AssistantPocToolCard[];
                const cardProducingTools = new Set<string>(CARD_PRODUCING_TOOLS);
                const usedCardTool = (event.toolsUsed ?? []).some((t) => cardProducingTools.has(t));
                if (broadcastCards.length === 0 && usedCardTool && this.conversationId !== null) {
                    this._fetchToolCardsForMessage(this.conversationId, event.messageId);
                }

                const incoming = (event.pendingProposals ?? []) as AssistantPocPendingProposal[];
                if (incoming.length > 0) {
                    const seen = new Set(this._pendingProposals$.value.map((p) => p.id));
                    const additions = incoming.filter((p) => !seen.has(p.id));
                    if (additions.length > 0) {
                        this._pendingProposals$.next([...this._pendingProposals$.value, ...additions]);
                    }
                }
                const completedQuestions = event.pendingQuestions ?? null;
                if (
                    completedQuestions !== null
                    && (this._pendingQuestions$.value === null
                        || this._pendingQuestions$.value.batchId !== completedQuestions.batchId)
                ) {
                    this._pendingQuestions$.next(completedQuestions);
                }
                this._disarmStreamWatchdog();
                this._isSending$.next(false);
                // Delay teardown so the follow-ups event (broadcast by a
                // separate post-completion Haiku job, typically lands
                // within 2–3s) has a chance to arrive on the channel
                // before we leave it. Without the delay we'd close the
                // subscription mid-flight and the chips would silently
                // never show up.
                this._scheduleDelayedTeardown(8000);
                break;
            }
            case 'ai.stream.follow-ups': {
                // Per-result chips arrive after `ai.stream.completed` (the
                // suggester runs as a separate Haiku job). Swap the empty
                // chip list the completed event seeded in for these
                // specific suggestions. Only honor for the current
                // conversation's most-recent assistant message — late
                // events from earlier turns get ignored.
                const streamingId = this._streamingAssistantMessageId;
                const lastAssistant = [...messages].reverse().find((m) => m.role === 'assistant');
                const targetId = lastAssistant?.messageId ?? null;
                if (event.messageId === targetId || event.messageId === streamingId) {
                    this._apiSuggestions$.next(event.suggestions ?? []);
                }
                break;
            }
            case 'ai.stream.pending-questions': {
                if (
                    this._pendingQuestions$.value === null
                    || this._pendingQuestions$.value.batchId !== event.batchId
                ) {
                    this._pendingQuestions$.next({
                        batchId: event.batchId,
                        leadIn: event.leadIn,
                        questions: event.questions,
                    });
                }
                this._messages$.next(messages.map((m) => {
                    if (m.role === 'assistant'
                        && m.messageId === event.messageId
                        && m.streamState === 'streaming') {
                        return { ...m, streamState: 'complete' as const };
                    }
                    return m;
                }));
                break;
            }
            case 'ai.stream.failed': {
                const friendly = event.error
                    ? `Sorry — the assistant ran into a problem: ${event.error}`
                    : 'Sorry — the assistant ran into a problem and couldn\'t finish that response.';
                this._messages$.next(messages.map((m) => {
                    if (m.role !== 'assistant') {
                        return m;
                    }
                    const isPrimary = m.messageId === event.messageId;
                    const isOrphanStream = !isPrimary && m.streamState === 'streaming';
                    if (!isPrimary && !isOrphanStream) {
                        return m;
                    }
                    return {
                        ...m,
                        streamState: 'failed' as const,
                        content: m.content && m.content.length > 0 ? m.content : friendly,
                        streamEndedAt: Date.now(),
                    };
                }));
                this._disarmStreamWatchdog();
                this._isSending$.next(false);
                this._teardownStream();
                break;
            }
        }
    }

    private _armStreamWatchdog(): void {
        this._disarmStreamWatchdog();
        this._streamWatchdog = setTimeout(() => {
            this._streamWatchdog = null;
            this._forceFinalizeStuckStream();
        }, STREAM_WATCHDOG_MS);
    }

    private _disarmStreamWatchdog(): void {
        if (this._streamWatchdog !== null) {
            clearTimeout(this._streamWatchdog);
            this._streamWatchdog = null;
        }
    }

    private _forceFinalizeStuckStream(): void {
        const stuck = this._messages$.value.some(
            (m) => m.role === 'assistant' && m.streamState === 'streaming',
        );
        if (!stuck && !this._isSending$.value) {
            return;
        }
        this._messages$.next(this._messages$.value.map((m) => {
            if (m.role !== 'assistant' || m.streamState !== 'streaming') {
                return m;
            }
            const settledThoughts = (m.thoughts ?? []).map((t) =>
                t.ok === undefined ? { ...t, ok: true } : t,
            );
            return {
                ...m,
                streamState: 'complete' as const,
                streamEndedAt: Date.now(),
                thoughts: settledThoughts,
            };
        }));
        this._isSending$.next(false);
        this._teardownStream();
    }

    private _teardownStream(): void {
        if (this._delayedTeardownTimer !== null) {
            clearTimeout(this._delayedTeardownTimer);
            this._delayedTeardownTimer = null;
        }
        if (this._streamSub) {
            this._streamSub.unsubscribe();
            this._streamSub = null;
        }
        if (this._streamHandle) {
            this._streamHandle.close();
            this._streamHandle = null;
        }
        this._disarmStreamWatchdog();
    }

    /**
     * Schedule teardown after a short grace period. Lets late-arriving
     * post-completion events (currently `ai.stream.follow-ups`) land on
     * the channel before we leave it. If the user sends another message
     * during the grace window, `sendMessage` calls `_teardownStream`
     * immediately, which cancels the pending timer and re-opens cleanly.
     */
    private _delayedTeardownTimer: ReturnType<typeof setTimeout> | null = null;

    private _scheduleDelayedTeardown(delayMs: number): void {
        if (this._delayedTeardownTimer !== null) {
            clearTimeout(this._delayedTeardownTimer);
        }
        this._delayedTeardownTimer = setTimeout(() => {
            this._delayedTeardownTimer = null;
            this._teardownStream();
        }, delayMs);
    }

    // -------------------------------------------------------------------------
    // Questions / proposals / feedback
    // -------------------------------------------------------------------------

    answerQuestions(answers: AssistantPocQuestionAnswer[], summary: string | null): void {
        const batch = this._pendingQuestions$.value;
        const conversationId = this.conversationId;
        if (batch === null || conversationId === null || this.submittingAnswers) {
            return;
        }
        this.submittingAnswers = true;
        if (summary && summary.length > 0) {
            this._messages$.next([
                ...this._messages$.value,
                { role: 'user', content: summary, timestamp: new Date() },
            ]);
        }
        this._isSending$.next(true);

        this._service.answerQuestions({
            conversationId,
            batchId: batch.batchId,
            answers,
            questionsEcho: batch.questions,
        }).subscribe({
            next: (res) => {
                this.submittingAnswers = false;
                this._isSending$.next(false);
                this._processChatEnvelope(res);
            },
            error: (err) => {
                this.submittingAnswers = false;
                this._isSending$.next(false);
                const status = err?.status ?? 0;
                const friendly = status === 422
                    ? 'These questions are no longer valid — please refresh or send a new message.'
                    : 'Sorry — I couldn\'t submit those answers. Please try again.';
                if (status === 422) {
                    this._pendingQuestions$.next(null);
                }
                this._messages$.next([
                    ...this._messages$.value,
                    {
                        role: 'assistant',
                        content: friendly,
                        timestamp: new Date(),
                        streamState: 'failed',
                    },
                ]);
            },
        });
    }

    closeQuestionCard(): void {
        if (this.submittingAnswers) {
            return;
        }
        this._pendingQuestions$.next(null);
    }

    updateThoughtsCollapsed(messageId: number, collapsed: boolean): void {
        this._messages$.next(this._messages$.value.map((m) =>
            m.role === 'assistant' && m.messageId === messageId
                ? { ...m, thoughtsCollapsed: collapsed }
                : m,
        ));
    }

    setFeedback(messageId: number, feedback: AssistantPocFeedback): void {
        this._feedbackByMessageId.set(messageId, feedback);
        this._feedback$.next(new Map(this._feedbackByMessageId));
    }

    getFeedback(messageId: number | undefined): AssistantPocFeedback | null {
        if (messageId === undefined) {
            return null;
        }
        return this._feedbackByMessageId.get(messageId) ?? null;
    }

    recordFeedback(interactionId: number, feedback: AssistantPocFeedback): Observable<unknown> {
        return this._service.recordFeedback(interactionId, feedback);
    }

    // ----- Taught-from-this-answer badge -----
    // Persistent client-side state so the "🎓 You taught Gable" badge
    // survives reloads / route changes. Keyed by interactionId (the
    // telemetry event id, stable for the assistant turn) so the badge
    // re-renders even after the chat session reloads the messages.

    private static readonly TAUGHT_STORAGE_KEY = 'gable.taughtInteractionIds';

    private _taughtInteractionIds: Set<number> = this._loadTaughtIds();

    private _loadTaughtIds(): Set<number> {
        try {
            const raw = LocalService.get(GableDrawerService.TAUGHT_STORAGE_KEY);
            if (!raw) { return new Set(); }
            const arr = JSON.parse(raw);
            return Array.isArray(arr) ? new Set(arr.filter((n) => typeof n === 'number')) : new Set();
        } catch {
            return new Set();
        }
    }

    private _persistTaughtIds(): void {
        try {
            LocalService.set(
                GableDrawerService.TAUGHT_STORAGE_KEY,
                JSON.stringify([...this._taughtInteractionIds]),
            );
        } catch {
            // localStorage unavailable (private browsing). Badge becomes
            // session-only — acceptable.
        }
    }

    markTaught(interactionId: number): void {
        this._taughtInteractionIds.add(interactionId);
        this._persistTaughtIds();
    }

    wasTaught(interactionId: number | undefined): boolean {
        return interactionId !== undefined && this._taughtInteractionIds.has(interactionId);
    }

    // ----- "Feedback submitted" lockout — once the user picks a chip + Send
    // (or Teach / Flag), we collapse the thumbs/panel into a green confirmation
    // and block further feedback on this interaction. Session-only on purpose:
    // a reload should let them re-feedback if they really want.

    private _feedbackSentInteractionIds = new Set<number>();

    markFeedbackSent(interactionId: number): void {
        this._feedbackSentInteractionIds.add(interactionId);
    }

    wasFeedbackSent(interactionId: number | undefined): boolean {
        return interactionId !== undefined && this._feedbackSentInteractionIds.has(interactionId);
    }

    // ----- Inline Teach Gable view (drawer mode only) -----

    openTeachInDrawer(
        data: import('../../components/v2-layout/ai-poc/teach-gable-dialog/teach-gable-dialog.component').TeachGableDialogData,
    ): void {
        this._teachData = data;
        this._drawerView$.next('teach');
    }

    closeTeachInDrawer(): void {
        this._teachData = null;
        this._drawerView$.next('chat');
    }

    get drawerView(): 'chat' | 'teach' {
        return this._drawerView$.value;
    }

    approveProposal(id: number, idempotencyKey: string) {
        return this._service.approveProposal(id, idempotencyKey);
    }

    rejectProposal(id: number) {
        return this._service.rejectProposal(id);
    }

    commitActionProposal(id: number, idempotencyKey: string, edits: ActionPreviewEdits) {
        return this._service.commitActionProposal(id, idempotencyKey, edits);
    }

    refineActionProposal(id: number, utilityActionId: string, edits: ActionPreviewEdits) {
        return this._service.refineActionProposal(id, utilityActionId, edits);
    }

    /**
     * Patch the final state of an action-preview tool card (committed /
     * cancelled). The card component reads `payload.state` in ngOnChanges
     * and shows the matching "Action confirmed." / "Action cancelled."
     * footer. Decoupling from ViewChildren means the UI survives whichever
     * change-detection cycle happens to run first.
     */
    patchActionPreviewState(
        proposalId: number,
        state: 'pending' | 'committing' | 'committed' | 'cancelled',
        result: Record<string, unknown> | null = null,
    ): void {
        const messages = this._messages$.value.map((msg) => {
            if (!msg.toolCards || msg.toolCards.length === 0) {
                return msg;
            }
            let touched = false;
            const nextCards = msg.toolCards.map((card) => {
                if (card.tool !== 'propose_action') {
                    return card;
                }
                const payload = card.payload as { proposal_id?: number; proposalId?: number } | null;
                const id = payload?.proposal_id ?? payload?.proposalId;
                if (id !== proposalId) {
                    return card;
                }
                touched = true;
                const next: Record<string, unknown> = {
                    ...(card.payload as Record<string, unknown>),
                    state,
                };
                if (result !== null) {
                    // Mirror the execute() envelope under `result` so the
                    // collapsed card can render counts / deeplinks without
                    // another round trip.
                    next['result'] = result;
                }
                return { ...card, payload: next };
            });
            return touched ? { ...msg, toolCards: nextCards } : msg;
        });
        this._messages$.next(messages);
    }

    /**
     * Patch the in-memory action-preview tool card on whichever assistant
     * message holds it. Used by the drawer-host after a successful /refine
     * round-trip — backend rewrote `preview` + `edits`, we swap them into
     * the existing toolCard payload so the OnPush card re-renders.
     */
    patchActionPreview(
        proposalId: number,
        next: { preview: unknown; edits: unknown },
    ): void {
        const messages = this._messages$.value.map((msg) => {
            if (!msg.toolCards || msg.toolCards.length === 0) {
                return msg;
            }
            let touched = false;
            const nextCards = msg.toolCards.map((card) => {
                if (card.tool !== 'propose_action') {
                    return card;
                }
                const payload = card.payload as { proposal_id?: number; proposalId?: number } | null;
                const id = payload?.proposal_id ?? payload?.proposalId;
                if (id !== proposalId) {
                    return card;
                }
                touched = true;
                return {
                    ...card,
                    payload: {
                        ...(card.payload as Record<string, unknown>),
                        preview: next.preview,
                        // edits are not part of the rendered payload but the
                        // backend echoes them; keep around for diagnostics.
                        edits: next.edits,
                    },
                };
            });
            return touched ? { ...msg, toolCards: nextCards } : msg;
        });
        this._messages$.next(messages);
    }

    removeProposal(id: number): void {
        this._pendingProposals$.next(this._pendingProposals$.value.filter((p) => p.id !== id));
    }

    appendMessage(msg: GableChatMessage): void {
        this._messages$.next([...this._messages$.value, msg]);
    }

    /**
     * Pusher silently drops events over ~10KB, which means a full report
     * payload can arrive in `ai.stream.completed` with `toolCards: []`. When
     * we detect that case (a card-producing tool ran but no cards came
     * through), pull the persisted cards from the message's metadata via
     * REST and patch them into the assistant bubble.
     */
    private _fetchToolCardsForMessage(conversationId: number, messageId: number): void {
        this._service.fetchToolCards(conversationId, messageId).subscribe({
            next: (res) => {
                const cards = res.toolCards ?? [];
                if (cards.length === 0) {
                    return;
                }
                this._messages$.next(this._messages$.value.map((m) => {
                    if (m.role === 'assistant' && m.messageId === messageId) {
                        return { ...m, toolCards: [...(m.toolCards ?? []), ...cards] };
                    }
                    return m;
                }));
            },
            error: () => {
                // Non-fatal: the user can re-ask if the card never lands.
            },
        });
    }

    private _processChatEnvelope(res: AssistantPocChatResponse): void {
        if (res.conversationId) {
            this.conversationId = res.conversationId;
        }
        const nextBatch = res.pendingQuestions ?? null;
        this._pendingQuestions$.next(nextBatch);

        const message = (res.message ?? '').trim();
        if (nextBatch === null && message !== '') {
            this._messages$.next([
                ...this._messages$.value,
                {
                    role: 'assistant',
                    content: message,
                    citations: res.citations ?? [],
                    toolsUsed: res.toolsUsed ?? [],
                    timestamp: new Date(),
                    streamState: 'complete',
                    interactionId: res.aiInteractionEventId ?? undefined,
                    toolCards: res.toolCards ?? [],
                },
            ]);
        }
        const incoming = res.pendingProposals ?? [];
        if (incoming.length > 0) {
            const seen = new Set(this._pendingProposals$.value.map((p) => p.id));
            const additions = incoming.filter((p) => !seen.has(p.id));
            if (additions.length > 0) {
                this._pendingProposals$.next([...this._pendingProposals$.value, ...additions]);
            }
        }
        this._apiSuggestions$.next(res.followUpSuggestions ?? []);
    }
}
